// Reads one bundle's render path before anything else downloads: the /ogs list, then scene.json.
// Notes: ~/comments/aos-threejs-poc/renderSettingsProbe_probeRenderSettings_one_small_read.md

import { LEGACY_SETTINGS, SCENE_FILE, parseRenderSettings, type RenderSettings } from "./renderSettings.ts";
import { DEFAULT_LOG_LEVEL } from "./ogsLog.ts";

/** One /ogs row, in either casing the AAM emits. */
export interface OgsRow {
  filename?: string;
  name?: string;
  fileUrl?: string;
  file_url?: string;
  size?: number;
  byte_size?: number;
}

export interface FetchedFile {
  ok: boolean;
  status: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

/** Injected so this stays free of the Vite env import and testable under plain node:test. */
export interface RenderSettingsProbeDeps {
  listOgsAssets(slug: string): Promise<OgsRow[] | unknown>;
  fetchFile(path: string, size?: number): Promise<FetchedFile>;
}

const rowName = (row: OgsRow): string | undefined => row?.filename ?? row?.name;
const rowUrl = (row: OgsRow): string | undefined => row?.fileUrl ?? row?.file_url;
const rowSize = (row: OgsRow): number | undefined => row?.size ?? row?.byte_size;

function refuse(error: string): RenderSettings {
  return { path: "legacy", logging: DEFAULT_LOG_LEVEL, source: "error", error };
}

/** A character's declared render path. Unreadable means legacy, and says why. */
export async function probeRenderSettings(slug: string | null | undefined, deps: RenderSettingsProbeDeps): Promise<RenderSettings> {
  if (!slug) return LEGACY_SETTINGS;
  let rows: unknown;
  try {
    rows = await deps.listOgsAssets(slug);
  } catch (e) {
    return refuse(`the /ogs collection for '${slug}' could not be listed (${message(e)})`);
  }
  if (!Array.isArray(rows)) return refuse(`the /ogs collection for '${slug}' is not a list of rows`);

  // No scene.json is not a refusal: a single-region or mesh-only bundle ships none.
  const row = (rows as OgsRow[]).find((r) => rowName(r) === SCENE_FILE);
  if (!row) return LEGACY_SETTINGS;

  const url = rowUrl(row);
  if (!url) return refuse(`the ${SCENE_FILE} row for '${slug}' carries no file URL`);
  try {
    const res = await deps.fetchFile(url, rowSize(row));
    if (!res.ok) return refuse(`${SCENE_FILE} for '${slug}': HTTP ${res.status}`);
    const text = new TextDecoder().decode(new Uint8Array(await res.arrayBuffer()));
    return parseRenderSettings(JSON.parse(text));
  } catch (e) {
    return refuse(`${SCENE_FILE} for '${slug}' could not be read (${message(e)})`);
  }
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
